import { SearchDataError, searchBusinesses, type SearchResponse, type SearchResult } from '@powerful/data';
import { useInfiniteQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { getSupabaseClient } from '../lib/supabase';
import { readPublicFeed, savePublicFeed } from '../lib/discovery/public-feed-cache';
import { serializeDiscoveryFilters, toSearchRequest, type DiscoveryFilters, type SearchOrigin } from '../lib/discovery/search-params';

export type SearchViewError = 'rate_limited' | 'network' | 'invalid_request' | 'unauthenticated' | 'unknown' | null;

export function searchQueryKey(filters: DiscoveryFilters, origin?: SearchOrigin) {
  return ['discovery-search', serializeDiscoveryFilters(filters), origin ? Number(origin.lat.toFixed(3)) : null, origin ? Number(origin.lng.toFixed(3)) : null] as const;
}

export interface UseSearchOptions {
  /**
   * T2.10 F1 (Claude): `false` enquanto a origem ainda resolve (sessão/perfil). Sem isto a Home
   * pedia o feed neutro e, 1 RTT depois, o feed com origem — duas chamadas à Edge (rate limit) e
   * um salto visível de cards.
   */
  enabled?: boolean;
}

/** Query paginada sobre a Edge T2.9; o front recebe estados prontos, não lógica de cursor. */
export function useSearch(filters: DiscoveryFilters, origin?: SearchOrigin, options: UseSearchOptions = {}) {
  const queryClient = useQueryClient();
  const needsLocation = filters.sort === 'distance' && !origin;
  const request = useMemo(() => toSearchRequest(needsLocation ? { ...filters, sort: 'relevance' } : filters, origin), [serializeDiscoveryFilters(filters), origin?.lat, origin?.lng, needsLocation]);
  const queryKey = searchQueryKey(filters, origin);
  const query = useInfiniteQuery<SearchResponse, SearchDataError>({
    queryKey,
    enabled: !needsLocation && options.enabled !== false,
    initialPageParam: undefined,
    queryFn: async ({ pageParam, signal }) => {
      const cursor = typeof pageParam === 'string' ? pageParam : undefined;
      try {
        return await searchBusinesses(getSupabaseClient(), { ...request, ...(cursor ? { cursor } : {}) }, signal);
      } catch (error) {
        if (cursor && error instanceof SearchDataError && (error.code === 'invalid_cursor' || error.code === 'cursor_mismatch')) {
          return { ...await searchBusinesses(getSupabaseClient(), request, signal), cursorReset: true };
        }
        throw error;
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 60_000,
    retry: (failureCount, error) => failureCount < 2 && error.code === 'network',
  });

  useEffect(() => {
    const pages = query.data?.pages;
    if (!pages?.length) return;
    let reset = -1;
    for (let index = pages.length - 1; index >= 0; index -= 1) {
      if (pages[index]?.cursorReset === true) { reset = index; break; }
    }
    if (reset > 0) {
      const fresh = pages[reset]!;
      queryClient.setQueryData<InfiniteData<SearchResponse>>(queryKey, { pages: [fresh], pageParams: [undefined] });
      return;
    }
    // O snapshot é somente um inventário público, sem dados de filtro, localização ou conta.
    const publicResults = pages.flatMap((page) => page.results);
    if (publicResults.length && typeof navigator !== 'undefined' && navigator.onLine) savePublicFeed(publicResults);
  }, [query.data, queryClient, queryKey]);

  const results: SearchResult[] = query.data?.pages.flatMap((page) => page.results) ?? [];
  const errorCode: SearchViewError = query.error instanceof SearchDataError
    ? query.error.code === 'rate_limited' ? 'rate_limited'
      : query.error.code === 'invalid_request' ? 'invalid_request'
        : query.error.code === 'unauthenticated' ? 'unauthenticated'
          : query.error.code === 'network' ? 'network' : 'unknown'
    : null;

  return {
    results,
    loadedCount: results.length,
    weights: query.data?.pages.at(-1)?.weights ?? null,
    hasMore: Boolean(query.hasNextPage),
    fetchMore: query.fetchNextPage,
    isLoading: query.isPending,
    isFetchingMore: query.isFetchingNextPage,
    isError: query.isError,
    errorCode,
    retryAfterSeconds: query.error instanceof SearchDataError ? query.error.retryAfterSeconds : undefined,
    refetch: query.refetch,
    offlineSnapshot: typeof navigator !== 'undefined' && !navigator.onLine ? readPublicFeed() : null,
    needsLocation,
  };
}
